"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import videojs from "video.js";
import type Player from "video.js/dist/types/player";
import { fetchTrailerPlayback } from "@/queries/hls.clinet";
import type { TrailerPlaySuccessResponse } from "@/types/hls";
import { getVideoType } from "./videoMimeType";
interface Params {
  open: boolean;
  trailerUuid: string;
}
interface Result {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  loading: boolean;
  error: string | null;
  ready: boolean;
  retry: () => void;
}
export function useTrailerPlayer(t0) {
  const $ = _c(12);
  const {
    open,
    trailerUuid
  } = t0;
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);
  const [attempt, setAttempt] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setError(null);
      setLoading(true);
      setReady(false);
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      setAttempt(_temp);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const retry = t1;
  let t2;
  if ($[1] !== open || $[2] !== trailerUuid) {
    t2 = () => {
      if (!open || !trailerUuid) {
        return;
      }
      let mounted = true;
      const init = async () => {
        setLoading(true);
        setError(null);
        setReady(false);
        await new Promise(_temp2);
        if (!mounted || !videoRef.current) {
          if (mounted) {
            setError("\u062E\u0637\u0627 \u062F\u0631 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u067E\u0644\u06CC\u0631");
          }
          setLoading(false);
          return;
        }
        let trailer;
        try {
          trailer = await fetchTrailerPlayback({
            trailerUuid
          });
        } catch {
          if (mounted) {
            setError("\u062E\u0637\u0627 \u062F\u0631 \u062F\u0631\u06CC\u0627\u0641\u062A \u062A\u0631\u06CC\u0644\u0631");
            setLoading(false);
          }
          return;
        }
        if (!mounted) {
          return;
        }
        if (!trailer.succeed) {
          setError("\u062E\u0637\u0627 \u062F\u0631 \u062F\u0631\u06CC\u0627\u0641\u062A \u062A\u0631\u06CC\u0644\u0631");
          setLoading(false);
          return;
        }
        let videoUrl = (trailer as TrailerPlaySuccessResponse).data.playback_url;
        if (videoUrl.startsWith("http://")) {
          videoUrl = videoUrl.replace("http://", "https://");
        }
        if (playerRef.current) {
          playerRef.current.dispose();
          playerRef.current = null;
        }
        if (!videoRef.current) {
          setError("\u062E\u0637\u0627 \u062F\u0631 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u067E\u0644\u06CC\u0631");
          setLoading(false);
          return;
        }
        const player = videojs(videoRef.current, {
          controls: true,
          autoplay: true,
          fill: true,
          preload: "auto",
          techOrder: ["html5"]
        });
        player.ready(() => {
          player.src({
            src: videoUrl,
            type: getVideoType(videoUrl)
          });
          player.load();
        });
        player.on("error", () => {
          const err = player.error();
          if (err?.code === 4) {
            player.src({
              src: videoUrl,
              type: "video/mp4"
            });
            player.load();
          } else {
            if (mounted) {
              setError(`خطا در پخش ویدیو: ${err?.message || "\u062E\u0637\u0627\u06CC \u0646\u0627\u0634\u0646\u0627\u062E\u062A\u0647"}`);
              setLoading(false);
              setReady(false);
            }
          }
        });
        const onReady = () => {
          if (mounted) {
            setLoading(false);
            setReady(true);
          }
        };
        player.on("canplaythrough", onReady);
        player.on("loadeddata", onReady);
        playerRef.current = player;
      };
      init();
      return () => {
        mounted = false;
        if (playerRef.current) {
          playerRef.current.dispose();
          playerRef.current = null;
        }
        setReady(false);
        setLoading(false);
        setError(null);
      };
    };
    $[1] = open;
    $[2] = trailerUuid;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== attempt || $[5] !== open || $[6] !== trailerUuid) {
    t3 = [open, trailerUuid, attempt];
    $[4] = attempt;
    $[5] = open;
    $[6] = trailerUuid;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== error || $[9] !== loading || $[10] !== ready) {
    t4 = {
      videoRef,
      loading,
      error,
      ready,
      retry
    };
    $[8] = error;
    $[9] = loading;
    $[10] = ready;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function _temp2(r) {
  return setTimeout(r, 50);
}
function _temp(n) {
  return n + 1;
}